import { memo, useState, useCallback, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TextField, Drawer, Box, Button, Typography } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
// Local files
import DrawerSkeleton from 'components/Common/BaseDrawerSkeleton/BaseDrawerSkeleton';
import BaseEventsList from 'components/Analyses/Common/BaseEventsList/BaseEventsList';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import usePenStudyAnalyses from 'hooks/usePenStudyAnalyses';
import useEvents from 'hooks/useEvents';
import { eventsSelectors } from "features/events";
import { penStudyAnalysesSelectors } from 'features/penStudyAnalyses';

const PenStudyAnalysisDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { pen_study_analysis_id } = useParams();
  const { getPenStudyAnalysis, clearLocalActivePenStudyAnalysis } = usePenStudyAnalyses();
  const { getPenStudyAnalysesEvents, clearLocalAnalysesEvents } = useEvents();
  const { data, pagination } = useCustomSelector(eventsSelectors.getAnalysesEventsData);
  const eventsFetching = useCustomSelector(eventsSelectors.analysesEventsIsFetching);
  const penStudyAnalysisFetching = useCustomSelector(penStudyAnalysesSelectors.penStudyAnalysisIsFetching);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [firstTimeEventsOpened, setFirstTimeEventsOpened] = useState(true);

  const fetchPenStudyAnalysis = useCallback((id) => {
    setOpen(true);
    getPenStudyAnalysis({ id })
    .then(({ payload, error }) => {
      if (!error) {
        const { data: { pen_study_analysis } } = payload;
        handleSetData(pen_study_analysis);
      }
    })
  }, [getPenStudyAnalysis]);

  const handleSetData = (pen_study_analysis) => {
    setResult(pen_study_analysis?.result);
  };

  const handleExiting = () => {
    setResult('');
    setExpanded(false);
    setFirstTimeEventsOpened(true);
    navigate(`/flow/fl30c`);
    clearLocalActivePenStudyAnalysis();
    clearLocalAnalysesEvents();
  };

  const handleClose = () => setOpen(false);

  const fetchPenStudyAnalysesEvents = useCallback((offset, pen_study_analysis_id) => {
    getPenStudyAnalysesEvents({ offset, pen_study_analysis_id })
  }, [ getPenStudyAnalysesEvents]);

  const handleChangeExpand = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (firstTimeEventsOpened) {
      fetchPenStudyAnalysesEvents(0, pen_study_analysis_id);
      setFirstTimeEventsOpened(false);
    }
  }

  useEffect(() => {
    if (!!pen_study_analysis_id) fetchPenStudyAnalysis(pen_study_analysis_id);
  }, [pen_study_analysis_id, fetchPenStudyAnalysis]);

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      SlideProps={{ onExited: handleExiting }}
      onClose={handleClose}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
            Request log
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
      { penStudyAnalysisFetching ? <DrawerSkeleton /> :
        <>
          <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'center', mb: '10px' }}>Job Id: {pen_study_analysis_id}</Typography>
          <TextField
            fullWidth
            margin='normal'
            label='Result'
            multiline
            minRows={5}
            maxRows={8}
            disabled
            value={result ?? ''}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#073842",
              }
            }}
          />
          <BaseEventsList
            data={data}
            pagination={pagination}
            analysisId={pen_study_analysis_id}
            eventsFetching={eventsFetching}
            fetchEvents={fetchPenStudyAnalysesEvents}
            expanded={expanded}
            handleChangeExpand={handleChangeExpand}
          />
        </>}
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <PenStudyAnalysisDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);